import { defineStore } from 'pinia'
import type { AxiosResponse } from 'axios'
import { client } from '@/services/client'
import { handleRequest } from '@/services/handleRequest'
import type { SafeAxiosResponse } from '@/services/handleRequest'
import type { Partner, PartnerPayload } from '@/types'
import { t } from '@/utils/helpers/translate'
import { toggleUnleashPartner } from '@/utils/setup-application/createUnleash'
import type { PartnerSignupPayload } from '@/components/Auth/Partner/types'

export type State = {
  partner: Partner | undefined
}

export const usePartnerUser = defineStore('partnerUser', {
  state: (): State => ({
    partner: undefined,
  }),
  actions: {
    setPartnerInfo(value: Partner): void {
      this.partner = value
    },

    clearPartnerInfo(): void {
      this.partner = undefined
    },

    async signUpPartner(
      payload: PartnerSignupPayload,
    ): Promise<SafeAxiosResponse<PartnerPayload>> {
      const response = await handleRequest({
        apiCall: (): Promise<AxiosResponse<PartnerPayload>> =>
          client.post('/partners', payload),
        expectedErrors: new Map([
          [404, ''],
          [422, t('Missing data: please check all required fields.')],
        ]),
      })

      return response
    },

    async loadPartnerInfo(): Promise<SafeAxiosResponse<PartnerPayload>> {
      const response = await handleRequest({
        apiCall: (): Promise<AxiosResponse<PartnerPayload>> =>
          client.get('/partners/me'),
        expectedErrors: new Map([[404, '']]),
      })

      if (response.status === 'success') {
        toggleUnleashPartner(response.data.partner.id)
        this.setPartnerInfo(response.data.partner)
      }

      return response
    },

    async updatePartnerInfo(
      payload: PartnerPayload,
    ): Promise<SafeAxiosResponse<void>> {
      const response = await handleRequest({
        apiCall: (): Promise<AxiosResponse<void>> =>
          client.put('/partners/me', payload),
        expectedErrors: new Map([
          [404, ''],
          [422, t('Invalid data: please check all required fields.')],
        ]),
      })

      return response
    },
  },
})
